import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const CityContext = createContext(null);

export const CityProvider = ({ children }) => {
  const { api, user } = useAuth();
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load cities on mount or when user changes
  useEffect(() => {
    const fetchCities = async () => {
      setLoading(true);  // Always set loading when starting fetch
      console.log('[CityContext] Fetching cities...');
      try {
        const response = await api.get('/cities');
        console.log('[CityContext] Cities loaded:', response.data?.length);
        setCities(response.data);
        
        // Get saved city from localStorage or default to first city
        const savedCityId = localStorage.getItem('selectedCityId');
        if (savedCityId) {
          const savedCity = response.data.find(c => c.id === savedCityId);
          if (savedCity) {
            console.log('[CityContext] Restored city from localStorage:', savedCity.name);
            setSelectedCity(savedCity);
          } else if (response.data.length > 0) {
            console.log('[CityContext] Saved city not found, using first city');
            setSelectedCity(response.data[0]);
            localStorage.setItem('selectedCityId', response.data[0].id);
          }
        } else if (response.data.length > 0) {
          console.log('[CityContext] No saved city, using first city:', response.data[0].name);
          setSelectedCity(response.data[0]);
          localStorage.setItem('selectedCityId', response.data[0].id);
        }
      } catch (error) {
        console.error('[CityContext] Failed to load cities:', error);
      } finally {
        setLoading(false);
        console.log('[CityContext] Loading complete');
      }
    };

    if (user) {
      fetchCities();
    } else {
      // No user - reset state
      setSelectedCity(null);
      setCities([]);
      setLoading(false);
    }
  }, [api, user]);

  // Save selected city to localStorage
  const switchCity = (city) => {
    setSelectedCity(city);
    localStorage.setItem('selectedCityId', city.id);
  };

  // Get city filter parameter for API calls
  const getCityFilter = () => {
    return selectedCity?.id || null;
  };

  return (
    <CityContext.Provider value={{
      cities,
      selectedCity,
      switchCity,
      getCityFilter,
      loading
    }}>
      {children}
    </CityContext.Provider>
  );
};

export const useCity = () => {
  const context = useContext(CityContext);
  if (!context) {
    throw new Error('useCity must be used within a CityProvider');
  }
  return context;
};

export default CityContext;
